/**
 * Notification Deep Link Resolver
 * 
 * NAVIGATION-FIRST: Always navigate if we can resolve a target.
 * Modal is ONLY for truly unresolvable notifications.
 */

/**
 * Extract booking ID from notification message text (legacy support)
 * Matches patterns like "booking #book_xxx" or "Booking #book_xxx"
 */
function extractBookingIdFromMessage(message) {
  if (!message) return null;
  const match = message.match(/booking\s*#(book_[a-zA-Z0-9_]+)/i);
  return match ? match[1] : null;
}

/**
 * Resolve the navigation action for a notification.
 * @param {Object} notification - The notification object
 * @param {string} currentUserRole - 'renter', 'host', 'platform_owner', etc.
 * @returns {Object} { kind: "navigate"|"modal", route?, params?, title?, body? }
 */
export function resolveNotificationAction(notification, currentUserRole) {
  // Extract all possible entity IDs
  // Try direct field first, then fallback to extracting from message
  // NOTE: Message extraction only works if the message contains the FULL booking ID
  const bookingIdFromMessage = extractBookingIdFromMessage(notification.message);
  const bookingId = notification.booking_id || 
                    notification.entity_id || 
                    // Only use extracted ID if it's at least 16 chars (book_ + 12 char hex)
                    (bookingIdFromMessage && bookingIdFromMessage.length >= 17 ? bookingIdFromMessage : null);
  const disputeId = notification.dispute_id;
  const vehicleId = notification.vehicle_id;
  const type = notification.type || "";
  
  let result = null;
  
  // PRIORITY 1: Use explicit deep_link if present
  if (notification.deep_link && notification.deep_link.route) {
    result = {
      kind: "navigate",
      route: notification.deep_link.route,
      params: notification.deep_link.params || {}
    };
  }
  
  // PRIORITY 2: Dispute-related notifications (check before booking since disputes have booking_id too)
  if (!result && (disputeId || type.includes("dispute"))) {
    if (currentUserRole === "platform_owner" || currentUserRole === "admin") {
      result = {
        kind: "navigate",
        route: "/financial-enforcement",
        params: disputeId ? { openDispute: disputeId, booking_id: bookingId } : {}
      };
    } else if (bookingId) {
      // Renter/Host viewing dispute → go to booking with extra_charges focus
      result = {
        kind: "navigate",
        route: `/bookings/${bookingId}`,
        params: { focus: "extra_charges" }
      };
    }
  }
  
  // PRIORITY 3: Booking-related notifications (if we have a booking_id)
  if (!result && bookingId) {
    const params = {};
    
    // Add focus param for specific sections
    if (type.includes("prehire")) {
      params.focus = "prehire";
    } else if (type.includes("posthire")) {
      params.focus = "posthire";
    } else if (type.includes("extra_charges") || type.includes("payment") || type.includes("charge")) {
      params.focus = "extra_charges";
    } else if (type.includes("extension")) {
      params.focus = "extensions";
    }
    
    result = {
      kind: "navigate",
      route: `/bookings/${bookingId}`,
      params
    };
  }
  
  // PRIORITY 4: Legal notifications
  if (!result && (type.includes("legal") || type.includes("terms") || type.includes("privacy"))) {
    result = {
      kind: "navigate",
      route: "/legal",
      params: {}
    };
  }
  
  // PRIORITY 5: Verification notifications
  if (!result && (type.includes("verification") || type.includes("identity") || type.includes("licence"))) {
    result = {
      kind: "navigate",
      route: "/verification",
      params: {}
    };
  }
  
  // PRIORITY 6: Vehicle notifications
  if (!result && vehicleId) {
    result = {
      kind: "navigate",
      route: `/vehicles/${vehicleId}`,
      params: {}
    };
  }
  
  // PRIORITY 7: Welcome / profile notifications
  if (!result && (type === "welcome" || type.includes("profile"))) {
    result = {
      kind: "navigate",
      route: "/profile",
      params: {}
    };
  }
  
  // FALLBACK: Modal only when truly unresolvable
  if (!result) {
    result = {
      kind: "modal",
      title: notification.title,
      body: notification.message
    };
  }
  
  // DEV MODE: Log navigation for debugging
  if (process.env.NODE_ENV === 'development' && result.kind === "navigate") {
    const url = buildNotificationUrl(result.route, result.params);
  }
  
  return result;
}

/**
 * Get icon color classes for notification type
 */
export function getNotificationIconColor(type) {
  if (type.includes("approved") || type.includes("accepted") || type === "welcome" || type.includes("paid") || type.includes("completed")) {
    return "text-green-600 bg-green-50";
  }
  if (type.includes("rejected") || type.includes("declined") || type.includes("cancelled")) {
    return "text-red-600 bg-red-50";
  }
  if (type.includes("dispute")) {
    return "text-amber-600 bg-amber-50";
  }
  if (type.includes("payment") || type.includes("refund") || type.includes("charges")) {
    return "text-blue-600 bg-blue-50";
  }
  if (type.includes("legal") || type.includes("terms")) {
    return "text-purple-600 bg-purple-50";
  }
  return "text-slate-600 bg-slate-50";
}

/**
 * Build URL with query params
 */
export function buildNotificationUrl(route, params) {
  if (!params || Object.keys(params).length === 0) {
    return route;
  }
  
  // Filter out empty values and id if already in route
  const queryParams = Object.entries(params)
    .filter(([key, value]) => value && (key !== "id" || !route.includes(value)))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  
  if (!queryParams) return route;
  return `${route}?${queryParams}`;
}
